import React, { useContext, useRef, useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { useRecoilState } from "recoil";
import { userInfoState, accessTokenState } from "../global-state";
import { SAND_CAT_WS } from "../config";
import UserAuth from "../auth/user-auth";
import { useEventEmitter } from "./event-context";

const WebSocketContext = React.createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [wsReady, setWsReady] = useState(false);
  const [userInfo] = useRecoilState(userInfoState);
  const [accessToken, setAccessToken] = useRecoilState(accessTokenState);
  const { emit } = useEventEmitter();

  const wsRef = useRef(null);
  const messageQueue = useRef({});
  const heartbeatInterval = useRef(null);
  const lastMessageTime = useRef(Date.now());
  const reconnectTimeout = useRef(null);
  const invisibleStartTime = useRef(null);
  const retryDuration = useRef(2000);
  const isPageVisible = useRef(true);
  const accessTokenRef = useRef(accessToken);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      console.log(`Visibility change: ${document.visibilityState}`);
      isPageVisible.current = document.visibilityState === "visible";
      if (isPageVisible.current) {
        if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
          reconnect();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const reconnect = async () => {
    console.log("Reconnecting WebSocket...");
    if (userInfo && userInfo.activeScope) {
      console.log("check user from web");
      await refreshUserTokenIfNeeded();
      connectWebSocket();
    }
  };

  const startHeartbeat = () => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
    }

    heartbeatInterval.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const timeSinceLastMessage = Date.now() - lastMessageTime.current;

        if (timeSinceLastMessage > 30000) {
          try {
            console.log("Sending PING");
            wsRef.current.send(JSON.stringify({ type: "PING" }));
          } catch (error) {
            console.warn("Failed to send PING:", error);
            cleanupConnection();
          }
        }

        if (!isPageVisible.current) {
          if (!invisibleStartTime.current) {
            invisibleStartTime.current = Date.now();
            console.log("Page is hidden, started tracking invisible time");
          }
          const invisibleDuration = Date.now() - invisibleStartTime.current;
          if (invisibleDuration > 600000) {
            console.log("Closing connection after 10 minutes of invisibility");
            cleanupConnection();
          }
        } else {
          if (invisibleStartTime.current) {
            console.log("Page is visible again, resetting invisible time");
          }
          invisibleStartTime.current = null;
        }
      }
    }, 30000);
  };

  const cleanupConnection = () => {
    console.log("Cleaning up WebSocket connection...");
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
    }
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsReady(false);
  };

  const refreshUserTokenIfNeeded = async () => {
    try {
      const { isAuthenticated, idToken } = await UserAuth.checkUserAuthState();
      console.log("check user from web: get user info");
      if (!isAuthenticated) {
        await UserAuth.signIn();
        return false;
      }
      if (idToken !== accessTokenRef.current) {
        console.log("Refresh token!");
        setAccessToken(idToken);
      }
      return true;
    } catch {
      console.log("check user from web: failed, signing in again...");
      await UserAuth.signIn();
      return false;
    }
  };

  const connectWebSocket = () => {
    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    console.log("Receive request to connect WebSocket.");

    let socketUrl = SAND_CAT_WS;
    const customAddress = localStorage.getItem("localServiceAddress");
    if (customAddress) {
      socketUrl = SAND_CAT_WS.replace("localhost", customAddress);
    }
    socketUrl += `?authorization=Bearer ${accessTokenRef.current}&scope=${userInfo.activeScope.id}`;
    console.log(`WebSocket connecting to ${socketUrl}`);

    const websocket = new WebSocket(socketUrl);

    websocket.onopen = () => {
      console.log("WebSocket Connection Established");
      setWsReady(true);
      wsRef.current = websocket;
      retryDuration.current = 2000;
      lastMessageTime.current = Date.now();
      startHeartbeat();
    };

    websocket.onmessage = event => {
      lastMessageTime.current = Date.now();
      const data = JSON.parse(event.data);
      if (data.type === "PONG") {
        console.log("Received PONG from server");
        return;
      }
      if (
        data.type === "SERVER_RESP" &&
        data.sig &&
        messageQueue.current[data.sig]
      ) {
        messageQueue.current[data.sig].resolve(data);
        delete messageQueue.current[data.sig];
      }
      if (data.type === "SERVER_DATA") {
        emit(data.target, data);
      }
    };

    websocket.onclose = async event => {
      console.log("WebSocket connection closed:", event);
      setWsReady(false);
      emit("_wsclosed_", null);
      if (event.reason === "UNAUTHENTICATED") {
        console.log("Refreshing token due to UNAUTHENTICATED");
        const tokenRefreshed = await refreshUserTokenIfNeeded();
        if (tokenRefreshed) {
          reconnect();
        } else {
          await UserAuth.signIn();
        }
        return;
      }
      if (isPageVisible.current) {
        reconnectTimeout.current = setTimeout(
          () => connectWebSocket(),
          retryDuration.current,
        );
        retryDuration.current *= 2;
        if (retryDuration.current > 20000) {
          retryDuration.current = 20000;
        }
      }
    };

    websocket.onerror = error => {
      console.error("WebSocket Error:", error);
      websocket.close();
    };

    wsRef.current = websocket;
  };

  const sendAndWaitWs = async (target, payload) => {
    const message = {
      target,
      sig: uuidv4(),
      payload,
      type: "CLIENT_REQ",
    };
    return new Promise((resolve, reject) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        console.log("Sending request with signature", message.sig);
        messageQueue.current[message.sig] = { resolve, reject };
        wsRef.current.send(JSON.stringify(message));
      } else {
        reject(new Error("WebSocket connection is not ready"));
      }
    });
  };

  const sendAndForgetWs = async (target, payload) => {
    const message = {
      target,
      sig: uuidv4(),
      payload,
      type: "CLIENT_DATA",
    };
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    } else {
      throw new Error("WebSocket connection is not ready");
    }
  };

  useEffect(() => {
    return () => {
      cleanupConnection();
    };
  }, []);

  useEffect(() => {
    if (!userInfo || !userInfo.activeScope) {
      cleanupConnection();
      return;
    }
    cleanupConnection();
    reconnect();
  }, [userInfo]);

  return (
    <WebSocketContext.Provider
      value={{
        ws: wsRef.current,
        wsReady,
        sendAndWaitWs,
        sendAndForgetWs,
        reconnect,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
